import { memo, useMemo } from 'react';
import type { FC } from 'react';

import { Popover, Radio } from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { FileIcon } from '@/components/Common/Icons';
import { useStores } from '@/components/hooks';
import { SharedFile } from '@/stores';
import { getPolicyAggregate } from '@/components/utils';
import { PASSWORD_PROTECTION } from '@/consts';

import FilePopup from '../FilePopup';
import { useFilesActionsContext } from '../../../hooks/useFilesActionContext';
import usePopupToggle from './usePopupToggle';
import getPathToSharedFolder from '../../../../../getPathToSharedFolder';
import styles from './FileRadio.module.scss';
import './popupContainerOverride.scss';

interface FileRadioProps {
    file: SharedFile;
    isSelected: boolean;
    isAccessDisabled: boolean;
}

const FileRadio: FC<FileRadioProps> = observer(({
    file,
    isSelected,
    isAccessDisabled,
}) => {
    const { userStore, recipientPageStore: { sharedFilesStore } } = useStores();

    // Only show password hint if the file has password protection enabled
    const hasPasswordProtection = useMemo(() => {
        const policyAggregate = file.policy ? getPolicyAggregate(file.policy) : null;
        return !!policyAggregate?.[PASSWORD_PROTECTION];
    }, [file.policy]);
    const effectiveHint = hasPasswordProtection ? sharedFilesStore.passwordHint : null;

    const { selectFile } = useFilesActionsContext();

    const isPopupTriggerActive = isSelected && !isAccessDisabled;
    const { onVisibilityChange, isPopupOpen } = usePopupToggle(isPopupTriggerActive);

    const changeFile = (event): void => {
        selectFile(file.file_id);
        if (file.is_folder && !isAccessDisabled && userStore.currentUserIdentity.id) {
            const path = getPathToSharedFolder(userStore.currentUserIdentity, file);
            window.open(path);
        }
    };

    return (
        <Radio.Button          
            className={classNames(
                styles.radio,
                styles['file-radio'],
                {
                    [styles.selected]: isSelected,
                    [styles.disabled]: isAccessDisabled,
                },
            )}
            value={file.file_id}
        >
            <Popover
            
                trigger="click"
                overlayClassName="file-popup-container"
                onOpenChange={onVisibilityChange}
                open={isPopupOpen}
                content={(
                    <FilePopup
                        policy={file.policy}
                        allowedApps={file.allowed_apps}
                        fileId={file.file_id}
                        ownerEmail={file.owner_email}
                        filename={file.filename}
                        hint={effectiveHint}
                    />
                )}
            >
                <div className={styles['file-radio-content']} onClick={changeFile}  >
                    <FileIcon
                        filename={file.filename}
                        isFolder={file.is_folder}
                        className={styles.icon}
                    />
                    <span
                        className={styles.filename}
                        title={file.filename}
                        data-testid="recipientPage_openFile"
                    >
                        {file.filename}
                    </span>
                    <div
                        className={styles['popup-trigger-placeholder']}
                        data-testid="recipientPage_popup"
                        onClick={(event) => {
                            event.stopPropagation();
                            selectFile(file.file_id);
                            onVisibilityChange(!isPopupOpen);
                        }}
                    >
                        {isPopupTriggerActive
                            && (
                                <>
                                    {isPopupOpen
                                        ? <CaretDownOutlined />
                                        : <CaretUpOutlined />}
                                </>
                            )}
                    </div>
                </div>
            </Popover>
        </Radio.Button>
    );
});

export default memo(FileRadio);
